import { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"

import { useLazyGetFulfilledOrdersQuery, useLazyGetOrdersQuery } from "store/api/ordersApi"
import { useAppDispatch, useAppSelector } from "store/hooks"
import { showEditOrderModalOld, showNewOrderModalOld } from "store/modalSlise"
import { setCurrentOrder } from "store/tempDataSlise"
import { orderStatusOptions } from "constants/SelectFieldOptions"
import { UITypes } from "types"
import styles from "assets/scss/index.module.scss"

import { convertOrdersToTableRows } from "utils/convertData"
import getErrorMessage from "utils/getErrorMessage"
import useExportCSV from "hooks/useExportCSV"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { DeleteOrderModal } from "modals/DeleteOrderModal"
import { OrderFulfillmentModalOld } from "modals/OrderFulfillmentModalOld"
import { ContentWrapper } from "components/common/ContentWrapper"
import { Pagination } from "components/common/Pagination"
import { ButtonNew } from "components/form/ButtonNew"
import { CheckboxNew } from "components/form/CheckboxNew"
import { StatusOfOrder } from "components/table/columnComponents/StatusOfOrder"
import NewTableWrapper from "components/table/TableWrapper"

// TABLE HEADER
const tableHeaderCells: UITypes.TableHeaderCell[] = [
  {
    title: "",
    size: "mini",
    name: "checkbox",
    cellComponent: "checkbox",
  },
  {
    title: "Order number",
    type: "sort-search",
    name: "id",
    size: "middle",
  },
  {
    title: "Creation Date",
    type: "sort-date-range",
    name: "create_date",
    size: "middle",
  },
  {
    title: "Shipping date",
    type: "date-range",
    name: "shipping_date",
    size: "middle",
  },
  {
    title: "Fulfillment date",
    type: "sort-date-range",
    name: "fulfillment_date",
    size: "middle",
  },
  {
    title: "Quantity",
    type: "sort-search",
    name: "quantity",
    size: "middle",
  },
  {
    title: "Status",
    type: "select",
    name: "status",
    options: orderStatusOptions,
    cellComponent: StatusOfOrder,
  },
  {
    title: "Warehouse",
    type: "sort-search",
    name: "warehouse",
  },
]

export const OrdersOld = () => {
  const dispatch = useAppDispatch()
  const [triggerGetOrders, { isFetching: isOrdersFetching, data: ordersData }] = useLazyGetOrdersQuery()
  const [triggerGetFulfilledOrders, { isFetching: isFulfilledFetching, data: fulfilledData }] =
    useLazyGetFulfilledOrdersQuery()
  const { urlQueryParams } = useUrlQueryParams()
  const currentOrder = useAppSelector(state => state.tempData.currentOrder)
  const [isFulfillChecked, checkFulfill] = useState(false)
  const [isFirstRender, setFirstRender] = useState(true)
  const [showOrderFulfillmentModal, setShowOrderFulfillmentModal] = useState(false)
  const [toBeDeletedOrder, setToBeDeletedOrder] = useState<number | null>(null)

  const relevantData = isFulfillChecked ? fulfilledData : ordersData
  const relevantFetching = isFulfillChecked ? isFulfilledFetching : isOrdersFetching

  const filteredTableHeaderCells = useMemo(() => {
    return tableHeaderCells.filter(item => isFulfillChecked || item.name !== "fulfillment_date")
  }, [tableHeaderCells, isFulfillChecked])

  const { handleExport } = useExportCSV({
    params: urlQueryParams,
    headerCells: filteredTableHeaderCells,
    meta: relevantData?.meta,
    fileName: "orders",
    convertData: convertOrdersToTableRows,
    useLazyQuery: isFulfillChecked ? useLazyGetFulfilledOrdersQuery : useLazyGetOrdersQuery,
  })

  const onSetCurrentOrder = (id?: number) => {
    const ordersList = relevantData?.data

    if (!id || !ordersList?.length) {
      dispatch(setCurrentOrder(undefined))
    } else {
      const currentOrder = ordersList.filter(item => item.id === id)[0]
      dispatch(setCurrentOrder(currentOrder))
    }
  }

  const onEditClick = (id: number) => {
    onSetCurrentOrder(id)
    dispatch(showEditOrderModalOld(true))
  }

  const onDeleteClick = (id: number) => {
    onSetCurrentOrder(id)
    setToBeDeletedOrder(id)
  }

  const onNewClick = () => {
    dispatch(showNewOrderModalOld(true))
  }

  const onFulfillClick = () => {
    if (!checkFulfillAbility()) return false
    setShowOrderFulfillmentModal(true)
  }

  const onCheckboxChange = (id: number) => {
    onSetCurrentOrder(id === currentOrder?.id ? undefined : id)
  }

  const checkFulfillAbility = () => {
    return currentOrder?.id && currentOrder?.status === "shipping" ? true : false
  }

  const getOrders = async (params: UITypes.Params) => {
    return isFulfillChecked ? await triggerGetFulfilledOrders(params) : await triggerGetOrders(params)
  }

  const handleOrderFulfillment = async () => {
    setShowOrderFulfillmentModal(false)
    getOrders(urlQueryParams)
  }

  const sendRequest = async (params: UITypes.Params) => {
    try {
      const res = await getOrders(params)
      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
      }
    } catch (error) {
      console.error(error)
    }
  }

  useEffect(() => {
    if (isFirstRender) {
      setFirstRender(false)
      return
    }
    sendRequest(urlQueryParams)
  }, [urlQueryParams, isFirstRender, isFulfillChecked])

  return (
    <>
      <ContentWrapper title="Orders history">
        <nav className={styles.tableNavWrapper}>
          <div>
            <ButtonNew onClick={onNewClick} color="blue">
              New order
            </ButtonNew>
          </div>
          <div className={styles.tableNavButtonsWrapper}>
            <ButtonNew
              disabled={!checkFulfillAbility()}
              onClick={onFulfillClick}
              color={!checkFulfillAbility() ? "grey" : "blue"}
            >
              Fulfill
            </ButtonNew>
            <CheckboxNew
              className={styles.tableNavItem}
              isReverse={true}
              id="fulfilled"
              isChecked={isFulfillChecked}
              onChange={event => checkFulfill(event?.currentTarget?.checked)}
            >
              Show fulfilled
            </CheckboxNew>
            <ButtonNew
              onClick={handleExport}
              className={styles.tableNavItem}
              disabled={relevantData?.meta?.total === 0}
            >
              Export
            </ButtonNew>
          </div>
        </nav>
        <NewTableWrapper
          tableName="contractor-orders"
          headerCells={filteredTableHeaderCells}
          data={convertOrdersToTableRows(relevantData?.data)}
          isLoading={relevantFetching}
          currentItemID={currentOrder?.id}
          onCheckboxChange={onCheckboxChange}
          onEditClick={onEditClick}
          onDeleteClick={onDeleteClick}
          clearAdditionalTags={() => dispatch(setCurrentOrder(undefined))}
          additionalTags={
            currentOrder
              ? [
                  {
                    label: "Order number",
                    values: [currentOrder.id],
                  },
                ]
              : []
          }
        />
        <Pagination meta={relevantData?.meta} />
      </ContentWrapper>
      {showOrderFulfillmentModal && (
        <OrderFulfillmentModalOld
          onCancel={() => setShowOrderFulfillmentModal(false)}
          onSuccess={handleOrderFulfillment}
        />
      )}
      {toBeDeletedOrder && <DeleteOrderModal orderId={toBeDeletedOrder} onCancel={() => setToBeDeletedOrder(null)} />}
    </>
  )
}
